export enum TimePeriod {
  OneMonth = '1_m',
  ThreeMonths = '3_m',
  SixMonths = '6_m',
  OneYear = '1_y',
  FiveYears = '5_y',
}

export interface StockDataPoint {
  date: string
  openPrice: number
  highPrice: number
  lowPrice: number
  closePrice: number
  volume: number
}

export interface DataPoint {
  date: string
  value: number
}

export const transformStockData = (stockData: StockDataPoint[]): DataPoint[] => {
  if (!stockData || !Array.isArray(stockData)) return []

  const sortedData = [...stockData].sort((a, b) => {
    const dateA = new Date(a.date)
    const dateB = new Date(b.date)

    // Check if dates are valid
    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0

    return dateB.getTime() - dateA.getTime()
  })

  return sortedData.map((item) => ({
    date: item.date,
    value: item.closePrice || 0,
  }))
}

// Function to filter data based on selected period
export const filterDataByPeriod = (data: DataPoint[], period: TimePeriod): DataPoint[] => {
  if (!data || !Array.isArray(data)) return []

  const now = new Date()
  const start = new Date(now)

  // Map TimePeriod to months/years
  const periodMap: Record<TimePeriod, { count: number; unit: 'm' | 'y' }> = {
    [TimePeriod.OneMonth]: { count: 1, unit: 'm' },
    [TimePeriod.ThreeMonths]: { count: 3, unit: 'm' },
    [TimePeriod.SixMonths]: { count: 6, unit: 'm' },
    [TimePeriod.OneYear]: { count: 1, unit: 'y' },
    [TimePeriod.FiveYears]: { count: 5, unit: 'y' },
  }

  const { count, unit } = periodMap[period] || { count: 1, unit: 'm' as const }

  if (unit === 'm') {
    start.setMonth(start.getMonth() - count)
  } else {
    start.setFullYear(start.getFullYear() - count)
  }

  // Find the closest available date that is <= start date
  const availableDates = data.map((item) => new Date(item.date)).filter((date) => !isNaN(date.getTime()))

  if (availableDates.length === 0) return []

  // Find dates <= start date
  const datesBeforeOrEqualStart = availableDates.filter((date) => date <= start)

  let actualStart: Date
  if (datesBeforeOrEqualStart.length > 0) {
    // Use the latest date <= start
    actualStart = datesBeforeOrEqualStart.reduce((latest, current) => (current > latest ? current : latest))
  } else {
    // If no dates <= start, use the earliest available date
    actualStart = availableDates.reduce((earliest, current) => (current < earliest ? current : earliest))
  }

  return data.filter((item: DataPoint) => {
    const itemDate = new Date(item.date)
    return !isNaN(itemDate.getTime()) && itemDate >= actualStart
  })
}
